import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Global from '../../../store/global-selectors';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { noop } from 'ui-components/default-props/noop';
import { RateBrowserProviderPropType } from '../../../prop-types/rate-browser-provider';
import { CurrencyService } from '../../../services/currency-service';
import { messages } from './messages';
import { RateListItem } from './rate-list-item';
import { RateWarningType } from './rate-warning-type';
import style from './rate-list.module.css';
import { sortByCost } from './sort-by';
import { emptyObject } from 'ui-components/default-props/empty';

const noRateErrorMessage = () => (
  <FormattedMessage
    defaultMessage='An error occurred calculating rates. No applicable services were available for the configured shipment.'
    description='rateList.noRates'
    id='CVxQsZ'
  />
);

export const RateList = ({ provider = emptyObject, selectedRate, onRateSelected = noop }) => {
  const { name, rates = [], errorMessage, isLoading } = provider;
  const intl = useIntl();
  const sortedRates = rates.sort(sortByCost);
  const defaultSellerCurrencyCode = useSelector((state) => Global.selectSellerDefaultCurrency(state));

  const getCurrencyCode = () => {
    let code;

    if (rates && rates.length > 0) {
      code = rates[0].rate.totalCost.code;
    } else {
      code = defaultSellerCurrencyCode;
    }
    const { symbol } = CurrencyService.getSymbolFromCode(code, 'USD', intl.locale);
    return symbol;
  };

  const isRateSelected = (currentRate, currentlySelectedRate) => {
    if (!currentlySelectedRate) {
      return false;
    }
    return currentRate.serviceId === currentlySelectedRate.serviceId && currentRate.packageTypeId === currentlySelectedRate.packageTypeId;
  };

  const warning = rates.some((rate) => rate.warning === RateWarningType.NotSupported);

  return (
    <div className={style['rate-list']}>
      <div className={style['rate-list-header']}>
        <div className={style['provider-name']}>{name}</div>
        <div className={style['estimated-rates']}>
          <>
            <FormattedMessage {...messages.estimatedRates} />
            {warning && (
              <Tooltip position='right-top'>
                <div className={style['header-tooltip']}>
                  <FontAwesomeIcon className={style['header-icon']} icon={faExclamationCircle} />
                </div>
                <TooltipContent>
                  <FormattedMessage {...messages.ratesUnavailable} values={{ currencyCode: getCurrencyCode() }} />
                </TooltipContent>
              </Tooltip>
            )}
          </>
        </div>
      </div>
      <div className={style['rate-list-content']}>
        {isLoading && (
          <div className={style['loading-indicator']}>
            <LoadingIndicator />
          </div>
        )}
        {!isLoading && rates.length === 0 && (
          <div className={style['rates-error-indicator']}>
            <div className={style['rates-error-message']}>{errorMessage || noRateErrorMessage()}</div>
          </div>
        )}
        {!isLoading && rates.length > 0 && (
          <div className={style['normal-content']}>
            {sortedRates.map((rate, index) => (
              <RateListItem
                key={index}
                className={style['rate-list-item']}
                onClick={onRateSelected}
                rate={rate}
                selected={isRateSelected(rate, selectedRate)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

RateList.propTypes = {
  provider: RateBrowserProviderPropType,
  selectedRate: PropTypes.object,
  onRateSelected: PropTypes.func,
};
